import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navbar, Nav, NavDropdown, Container, Button } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const NavigationBar = () => {
  const { user, logout, isAdmin, canManagePages, canAccessAccounting, canAccessQuickBooks, canAccessDocSign, canAccessApplications, canAccessWaterUsage, canAccessInsurance, canAccessCameras, canAccessTraffic, canAccessDropbox, canManagePortalDocs, canAccessDriverTablets, canAccessDoors, canAccessFuel } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const barRef = useRef(null);
  const menuOpenRef = useRef(false);
  menuOpenRef.current = menuOpen;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // The sidebar hangs under this bar (sticky top + 100vh height in index.css),
  // so keep --navbar-height equal to the bar's real height or every page
  // scrolls by the difference. Ignored while the phone menu is open.
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const measure = () => {
      if (menuOpenRef.current) return;
      root.style.setProperty('--navbar-height', `${Math.round(el.getBoundingClientRect().height)}px`);
    };
    measure();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => {
      if (ro) ro.disconnect();
      root.style.removeProperty('--navbar-height');
    };
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/login');
  };

  const closeMenuOnPageLink = (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || href === '#' || href.startsWith('#')) return;
    setMenuOpen(false);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'Admin': return 'role-admin';
      case 'Truck Driver': return 'role-truck-driver';
      case 'Office': return 'role-office';
      case 'Lanita Repair': return 'role-lanita-repair';
      case 'Office Admin': return 'role-office-admin';
      default: return 'bg-secondary';
    }
  };

  return (
    <Navbar ref={barRef} bg="white" expand="lg" sticky="top" className="shadow-sm border-bottom" expanded={menuOpen} onToggle={setMenuOpen}>
      <Container fluid>
        <Navbar.Brand as={Link} to="/dashboard" className="d-flex align-items-center" onClick={() => setMenuOpen(false)}>
          <img 
            src="/LanitaSpecializedLogo.jpg" 
            alt="Lanita Specialized Logo" 
            height="40"
            className="me-2"
            style={{ objectFit: 'contain' }}
          />
          <span className="fw-bold text-gradient d-none d-md-inline">Portal</span>
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav" onClick={closeMenuOnPageLink}>
          {/* Page links live in the collapsed (phone/tablet) menu only. From lg
              up the sidebar shows the same links, and repeating them here made
              the bar wrap to two or three lines. */}
          <Nav className="me-auto d-lg-none">
            <Nav.Link as={Link} to="/dashboard">
              <i className="bi bi-house me-1"></i>Dashboard
            </Nav.Link>
            <Nav.Link as={Link} to="/pages">
              <i className="bi bi-file-earmark-text me-1"></i>Pages
            </Nav.Link>
            <Nav.Link as={Link} to="/incidents">
              <i className="bi bi-exclamation-triangle me-1"></i>Incidents
            </Nav.Link>
            <Nav.Link as={Link} to="/passwords">
              <i className="bi bi-key-fill me-1"></i>Passwords
            </Nav.Link>
            <Nav.Link as={Link} to="/notes">
              <i className="bi bi-file-text me-1"></i>Notes
            </Nav.Link>
            <Nav.Link as={Link} to="/employees">
              <i className="bi bi-people-fill me-1"></i>Employees
            </Nav.Link>
            {canAccessAccounting() && (
              <Nav.Link as={Link} to="/accounting">
                <i className="bi bi-calculator me-1"></i>Accounting
              </Nav.Link>
            )}
            {canAccessQuickBooks() && (
              <Nav.Link as={Link} to="/quickbooks">
                <i className="bi bi-receipt-cutoff me-1"></i>QuickBooks
              </Nav.Link>
            )}
            {canAccessFuel() && (
              <Nav.Link as={Link} to="/fuel">
                <i className="bi bi-fuel-pump me-1"></i>Fuel
              </Nav.Link>
            )}
            {canAccessDocSign() && (
              <Nav.Link as={Link} to="/docsign">
                <i className="bi bi-pen me-1"></i>DocSign
              </Nav.Link>
            )}
            {canAccessApplications() && (
              <Nav.Link as={Link} to="/applications">
                <i className="bi bi-person-vcard me-1"></i>Applications
              </Nav.Link>
            )}
            {canAccessWaterUsage() && (
              <Nav.Link as={Link} to="/water-usage">
                <i className="bi bi-droplet-half me-1"></i>Water Usage
              </Nav.Link>
            )}
            {canAccessInsurance() && (
              <Nav.Link as={Link} to="/insurance">
                <i className="bi bi-shield-check me-1"></i>Insurance
              </Nav.Link>
            )}
            {canAccessDropbox() && (
              <Nav.Link as={Link} to="/dropbox">
                <i className="bi bi-cloud-arrow-up me-1"></i>Dropbox
              </Nav.Link>
            )}
            {canManagePortalDocs() && (
              <Nav.Link as={Link} to="/driver-docs">
                <i className="bi bi-folder2-open me-1"></i>Driver Docs
              </Nav.Link>
            )}
            {canAccessCameras() && (
              <Nav.Link as={Link} to="/cameras">
                <i className="bi bi-camera-video me-1"></i>Cameras
              </Nav.Link>
            )}
            {canAccessTraffic() && (
              <Nav.Link as={Link} to="/traffic">
                <i className="bi bi-activity me-1"></i>Traffic
              </Nav.Link>
            )}
            {canAccessDriverTablets() && (
              <Nav.Link as={Link} to="/driver-tablets">
                <i className="bi bi-truck me-1"></i>Driver Tablets
              </Nav.Link>
            )}
            {canAccessDoors() && (
              <Nav.Link as={Link} to="/doors">
                <i className="bi bi-door-open me-1"></i>Doors
              </Nav.Link>
            )}
            <Nav.Link as={Link} to="/plugins">
              <i className="bi bi-puzzle me-1"></i>Plugins
            </Nav.Link>
            {(isAdmin || canManagePages()) && (
              <NavDropdown 
                title={<><i className="bi bi-gear me-1"></i>{isAdmin ? 'Admin' : 'Manage'}</>} 
                id="admin-dropdown"
              >
                {isAdmin && (
                  <>
                    <NavDropdown.Item as={Link} to="/admin/dashboard">
                      <i className="bi bi-speedometer2 me-2"></i>Dashboard
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/admin/users">
                      <i className="bi bi-people me-2"></i>Users
                    </NavDropdown.Item>
                  </>
                )}
                {canManagePages() && (
                  <NavDropdown.Item as={Link} to="/admin/pages">
                    <i className="bi bi-file-earmark-plus me-2"></i>Pages
                  </NavDropdown.Item>
                )}
              </NavDropdown>
            )}
          </Nav>

          <Nav className="ms-auto">
            {/* Theme Toggle Button */}
            <Button
              variant="link"
              className="theme-toggle me-2"
              onClick={toggleTheme}
              title={`Switch to ${isDark ? 'light' : 'dark'} theme`}
            >
              <i className={`bi ${isDark ? 'bi-sun' : 'bi-moon'}`}></i>
            </Button>
            
            <NavDropdown
              title={
                <span>
                  <i className="bi bi-person-circle me-1"></i>
                  {user?.username}
                  <span className={`badge ms-2 ${getRoleBadgeClass(user?.role)}`}>
                    {user?.role}
                  </span>
                </span>
              }
              id="user-dropdown"
              align="end"
            >
              <NavDropdown.Item as={Link} to="/profile">
                <i className="bi bi-person me-2"></i>Profile
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to="/change-password">
                <i className="bi bi-key me-2"></i>Change Password
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item onClick={handleLogout}>
                <i className="bi bi-box-arrow-right me-2"></i>Logout
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavigationBar; 